import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  Trash2,
  ChevronRight,
  ChevronDown,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Eye,
  EyeOff,
  ArrowUp,
  ArrowDown,
} from 'lucide-react'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import { apiGet, apiPost, apiPut, apiDelete } from '../lib/api'

type MenuItem = {
  id: string
  parentId: string | null
  label: string
  url: string
  target: '_self' | '_blank'
  sortOrder: number
  isActive: boolean
}

export function MenuPage() {
  const qc = useQueryClient()
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [flash, setFlash] = useState<{ tipo: 'ok' | 'err'; msg: string } | null>(null)
  const [adding, setAdding] = useState<{ parentId: string | null } | null>(null)
  const [newLabel, setNewLabel] = useState('')
  const [newUrl, setNewUrl] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['menu-items'],
    queryFn: () => apiGet<{ data: MenuItem[] }>('/api/admin/menu-items'),
  })

  const tree = useMemo(() => {
    const items = data?.data ?? []
    const roots = items.filter((i) => !i.parentId).sort((a, b) => a.sortOrder - b.sortOrder)
    const childrenOf = (pid: string) =>
      items.filter((i) => i.parentId === pid).sort((a, b) => a.sortOrder - b.sortOrder)
    return { roots, childrenOf, all: items }
  }, [data])

  function notify(tipo: 'ok' | 'err', msg: string) {
    setFlash({ tipo, msg })
    setTimeout(() => setFlash(null), 3000)
  }

  function refresh() {
    qc.invalidateQueries({ queryKey: ['menu-items'] })
  }

  const createMut = useMutation({
    mutationFn: (payload: Partial<MenuItem>) =>
      apiPost<MenuItem>('/api/admin/menu-items', {
        ...payload,
        target: payload.target === '_blank' ? 'blank' : 'self',
      }),
    onSuccess: () => {
      notify('ok', 'Entrada creada')
      setAdding(null)
      setNewLabel('')
      setNewUrl('')
      refresh()
    },
    onError: (e: Error) => notify('err', e.message || 'Error al crear'),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Partial<MenuItem>) =>
      apiPut<MenuItem>(`/api/admin/menu-items/${id}`, {
        ...body,
        ...(body.target ? { target: body.target === '_blank' ? 'blank' : 'self' } : {}),
      }),
    onSuccess: () => refresh(),
    onError: (e: Error) => notify('err', e.message || 'Error al actualizar'),
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiDelete<{ ok: true }>(`/api/admin/menu-items/${id}`),
    onSuccess: () => {
      notify('ok', 'Entrada eliminada')
      refresh()
    },
    onError: (e: Error) => notify('err', e.message || 'Error al eliminar'),
  })

  function move(item: MenuItem, dir: -1 | 1) {
    const siblings = item.parentId ? tree.childrenOf(item.parentId) : tree.roots
    const idx = siblings.findIndex((s) => s.id === item.id)
    const swap = siblings[idx + dir]
    if (!swap) return
    updateMut.mutate({ id: item.id, sortOrder: swap.sortOrder })
    updateMut.mutate({ id: swap.id, sortOrder: item.sortOrder })
  }

  function toggleExpand(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function startAdd(parentId: string | null) {
    setAdding({ parentId })
    setNewLabel('')
    setNewUrl('')
    if (parentId) setExpanded((prev) => new Set(prev).add(parentId))
  }

  function confirmAdd() {
    if (!adding) return
    const label = newLabel.trim()
    const url = newUrl.trim()
    if (!label) {
      notify('err', 'Falta la etiqueta')
      return
    }
    const siblings = adding.parentId ? tree.childrenOf(adding.parentId) : tree.roots
    const nextOrder = (siblings.at(-1)?.sortOrder ?? 0) + 1
    createMut.mutate({
      parentId: adding.parentId,
      label,
      url,
      target: '_self',
      sortOrder: nextOrder,
      isActive: true,
    })
  }

  function renderItem(item: MenuItem, depth: number) {
    const children = tree.childrenOf(item.id)
    const isExpanded = expanded.has(item.id)
    const hasChildren = children.length > 0
    const siblings = item.parentId ? tree.childrenOf(item.parentId) : tree.roots
    const idx = siblings.findIndex((s) => s.id === item.id)
    const canUp = idx > 0
    const canDown = idx < siblings.length - 1

    return (
      <div key={item.id}>
        <div
          className="group flex items-center gap-2 rounded-lg border border-ink-100 bg-white p-2 hover:border-ink-300"
          style={{ marginLeft: depth * 24 }}
        >
          {hasChildren ? (
            <button
              type="button"
              onClick={() => toggleExpand(item.id)}
              className="grid size-6 place-items-center rounded text-ink-500 hover:bg-ink-100"
            >
              {isExpanded ? (
                <ChevronDown className="size-3.5" />
              ) : (
                <ChevronRight className="size-3.5" />
              )}
            </button>
          ) : (
            <span className="inline-block size-6" />
          )}

          <input
            type="text"
            value={item.label}
            onChange={(e) =>
              updateMut.mutate({ id: item.id, label: e.target.value })
            }
            className="w-44 rounded-md border border-transparent bg-transparent px-2 py-1 text-sm font-medium outline-none focus:border-ink-200 focus:bg-white"
          />

          <input
            type="text"
            value={item.url}
            placeholder="/ruta o https://…"
            onChange={(e) => updateMut.mutate({ id: item.id, url: e.target.value })}
            className="flex-1 rounded-md border border-transparent bg-transparent px-2 py-1 font-mono text-xs outline-none focus:border-ink-200 focus:bg-white"
          />

          <select
            value={item.target}
            onChange={(e) =>
              updateMut.mutate({
                id: item.id,
                target: e.target.value as '_self' | '_blank',
              })
            }
            className="rounded-md border border-ink-200 bg-white px-2 py-1 text-xs"
          >
            <option value="_self">misma pestaña</option>
            <option value="_blank">nueva pestaña</option>
          </select>

          <button
            type="button"
            title={item.isActive ? 'Ocultar' : 'Mostrar'}
            onClick={() =>
              updateMut.mutate({ id: item.id, isActive: !item.isActive })
            }
            className="grid size-7 place-items-center rounded text-ink-500 hover:bg-ink-100"
          >
            {item.isActive ? (
              <Eye className="size-3.5 text-emerald-600" />
            ) : (
              <EyeOff className="size-3.5 text-ink-400" />
            )}
          </button>

          <div className="flex items-center">
            <button
              type="button"
              disabled={!canUp}
              onClick={() => move(item, -1)}
              className="grid size-7 place-items-center rounded text-ink-500 hover:bg-ink-100 disabled:opacity-30"
            >
              <ArrowUp className="size-3.5" />
            </button>
            <button
              type="button"
              disabled={!canDown}
              onClick={() => move(item, 1)}
              className="grid size-7 place-items-center rounded text-ink-500 hover:bg-ink-100 disabled:opacity-30"
            >
              <ArrowDown className="size-3.5" />
            </button>
          </div>

          {!item.parentId && (
            <button
              type="button"
              title="Añadir sub-entrada"
              onClick={() => startAdd(item.id)}
              className="grid size-7 place-items-center rounded text-ink-500 hover:bg-ink-100"
            >
              <Plus className="size-3.5" />
            </button>
          )}

          <button
            type="button"
            title="Eliminar"
            onClick={() => {
              if (
                hasChildren
                  ? confirm(`Eliminar "${item.label}" y sus ${children.length} sub-entradas?`)
                  : confirm(`Eliminar "${item.label}"?`)
              )
                deleteMut.mutate(item.id)
            }}
            className="grid size-7 place-items-center rounded text-red-500 hover:bg-red-50"
          >
            <Trash2 className="size-3.5" />
          </button>
        </div>

        {isExpanded && hasChildren && (
          <div className="mt-1 space-y-1">
            {children.map((c) => renderItem(c, depth + 1))}
          </div>
        )}

        {adding?.parentId === item.id && (
          <div
            className="mt-1 flex items-center gap-2 rounded-lg border border-dashed border-brand-300 bg-brand-50 p-2"
            style={{ marginLeft: (depth + 1) * 24 }}
          >
            <span className="inline-block size-6" />
            <input
              type="text"
              value={newLabel}
              autoFocus
              placeholder="Etiqueta"
              onChange={(e) => setNewLabel(e.target.value)}
              className="w-44 rounded-md border border-ink-200 px-2 py-1 text-sm outline-none focus:border-brand-500"
            />
            <input
              type="text"
              value={newUrl}
              placeholder="/ruta"
              onChange={(e) => setNewUrl(e.target.value)}
              className="flex-1 rounded-md border border-ink-200 px-2 py-1 font-mono text-xs outline-none focus:border-brand-500"
            />
            <button
              type="button"
              onClick={confirmAdd}
              className="rounded-md bg-brand-600 px-3 py-1 text-xs font-semibold text-white hover:bg-brand-700"
            >
              Crear
            </button>
            <button
              type="button"
              onClick={() => setAdding(null)}
              className="rounded-md border border-ink-200 px-3 py-1 text-xs text-ink-600 hover:bg-ink-100"
            >
              Cancelar
            </button>
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      <Topbar
        title="Menú del header"
        subtitle="Configura la barra superior del sitio público. Soporta 2 niveles: padre y sub-entrada."
        actions={
          <>
            {data && <Badge variant="info">{tree.all.length} entradas</Badge>}
            <button
              type="button"
              onClick={() => startAdd(null)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700"
            >
              <Plus className="size-3.5" />
              Nueva entrada
            </button>
          </>
        }
      />

      {flash && (
        <div
          className={`mx-6 mt-4 flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
            flash.tipo === 'ok'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          {flash.tipo === 'ok' ? (
            <CheckCircle2 className="size-4" />
          ) : (
            <AlertCircle className="size-4" />
          )}
          <span>{flash.msg}</span>
        </div>
      )}

      <div className="p-6">
        {isLoading ? (
          <div className="flex items-center gap-2 text-ink-500">
            <Loader2 className="size-4 animate-spin" /> Cargando menú…
          </div>
        ) : tree.roots.length === 0 && !adding ? (
          <div className="rounded-2xl border border-dashed border-ink-200 bg-white p-10 text-center">
            <h3 className="text-sm font-semibold text-ink-900">Sin entradas</h3>
            <p className="mt-1 text-xs text-ink-500">
              Crea la primera entrada con el botón de arriba.
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            {tree.roots.map((r) => renderItem(r, 0))}
            {adding?.parentId === null && (
              <div className="flex items-center gap-2 rounded-lg border border-dashed border-brand-300 bg-brand-50 p-2">
                <span className="inline-block size-6" />
                <input
                  type="text"
                  value={newLabel}
                  autoFocus
                  placeholder="Etiqueta"
                  onChange={(e) => setNewLabel(e.target.value)}
                  className="w-44 rounded-md border border-ink-200 px-2 py-1 text-sm outline-none focus:border-brand-500"
                />
                <input
                  type="text"
                  value={newUrl}
                  placeholder="/ruta"
                  onChange={(e) => setNewUrl(e.target.value)}
                  className="flex-1 rounded-md border border-ink-200 px-2 py-1 font-mono text-xs outline-none focus:border-brand-500"
                />
                <button
                  type="button"
                  onClick={confirmAdd}
                  className="rounded-md bg-brand-600 px-3 py-1 text-xs font-semibold text-white hover:bg-brand-700"
                >
                  Crear
                </button>
                <button
                  type="button"
                  onClick={() => setAdding(null)}
                  className="rounded-md border border-ink-200 px-3 py-1 text-xs text-ink-600 hover:bg-ink-100"
                >
                  Cancelar
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}
